/// <amd-module name="Release/Helpers/Permission" />
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssSecurityService } from "VSS/Platform/Security";
import { IVssUserClaimsService, UserClaims } from "VSS/Platform/UserClaims";
import { IVssFeatureService } from "VSS/Platform/Feature";
import { format } from "VSS/Core/Util/String";
import { ITfsPageService } from "Tfs/Platform/Page";

export const enum ReleaseManagementSecurityPermissions {
    None = 0,
    ViewReleaseDefinition = 1,
    EditReleaseDefinition = 2,
    DeleteReleaseDefinition = 4,
    ManageReleaseApprovers = 8,
    ManageReleases = 16,
    ViewReleases = 32,
    CreateReleases = 64,
    EditReleaseEnvironment = 128,
    DeleteReleaseEnvironment = 256,
    AdministerReleasePermissions = 512,
    DeleteReleases = 1024,
    ManageDeployments = 2048,
    ManageReleaseSettings = 4096
}

export const enum ReleaseManagementUISecurityPermissions {
    None = 0,
    ViewTaskEditor = 1,
    ViewCDWorkflowEditor = 2,
    ExportReleaseDefinition = 4,
    ViewLegacyUI = 8,
    DeploymentSummaryAcrossProjects = 16,
    ViewExternalArtifactCommitsAndWorkItems = 32
}

export const ReleaseManagementNamespace = "C788C23E-1B46-4162-8F5E-D7585343B5DE";
export const ReleaseManagementUINamespace = "7c7d32f7-0e86-4cd6-892e-b35dbba870bd";
export const ReleaseManagementUIPermissionToken = "/ReleaseManagementUI";

export function hasRmPermissions(pageContext: IVssPageContext, folderPath: string | undefined | null, definitionId: number | undefined | null, permissionBit: ReleaseManagementSecurityPermissions): boolean {
    // override security permission.
    const featureService: IVssFeatureService = pageContext.getService<IVssFeatureService>("IVssFeatureService");
    if (featureService.isFeatureFlagEnabled(OverrideSecurityPermissionFeature)) {
        return true;
    }

    const token = getFullSecurityToken(pageContext, folderPath, definitionId);
    return pageContext.getService<IVssSecurityService>("IVssSecurityService").hasPermission(ReleaseManagementNamespace, token, permissionBit);
}

export function hasRmEnvironmentPermissions(pageContext: IVssPageContext, folderPath: string, definitionId: number, environmentId: number, permissionBit: ReleaseManagementSecurityPermissions): boolean {
    // override security permission.
    const featureService: IVssFeatureService = pageContext.getService<IVssFeatureService>("IVssFeatureService");
    if (featureService.isFeatureFlagEnabled(OverrideSecurityPermissionFeature)) {
        return true;
    }

    const token = getFullSecurityToken(pageContext, folderPath, definitionId, environmentId);
    return pageContext.getService<IVssSecurityService>("IVssSecurityService").hasPermission(ReleaseManagementNamespace, token, permissionBit);
}

export function hasRmUIPermissions(pageContext: IVssPageContext, permissionBit: ReleaseManagementUISecurityPermissions): boolean {
    return pageContext.getService<IVssSecurityService>("IVssSecurityService").hasPermission(ReleaseManagementUINamespace, ReleaseManagementUIPermissionToken, permissionBit);
}

export function userHasMemberClaim(pageContext: IVssPageContext): boolean {
    return pageContext.getService<IVssUserClaimsService>("IVssUserClaimsService").hasClaim(UserClaims.Member as any);
}

export function getFullSecurityToken(pageContext: IVssPageContext, folderPath: string | undefined | null, definitionId: number | undefined | null, definitionEnvironmentId?: number | undefined | null): string {
    const tfsPageData = pageContext.getService<ITfsPageService>("ITfsPageService").getData();
    let token = "";
    if (tfsPageData && tfsPageData.project) {
        token = tfsPageData.project.id;
    }

    const folderPathToken = createFolderPathSecurityToken(folderPath, definitionId, definitionEnvironmentId);
    if (folderPathToken) {
        token = token + "/" + folderPathToken;
    }

    return token;
}

export function createFolderPathSecurityToken(folderPath: string | undefined | null, definitionId: number | undefined | null, definitionEnvironmentId?: number | undefined | null): string {
    let token = "";
    if (folderPath && folderPath !== pathSeparator) {
        // Folder path is of format: "\\folder1\\folder2"
        // It should be converted to: folder1/folder2 for it to be a valid token
        token = folderPath.startsWith(pathSeparator)
            ? folderPath.substring(1)
            : folderPath;
        token = token.split(pathSeparator).join("/");
    }

    if (definitionId && definitionId > 0) {
        if (token) {
            token = token + "/";
        }

        token = (token + definitionId);

        if (definitionEnvironmentId && definitionEnvironmentId > 0) {
            token = format("{0}/Environment/{1}", token, definitionEnvironmentId);
        }
    }

    return token;
}

const pathSeparator = "\\";
const OverrideSecurityPermissionFeature: string = "WebAccess.ReleaseManagement.HidePermissionWarning";